import React, { useMemo } from 'react';
import { Ban, Lock, LockOpen, ShieldAlert, Trash2 } from 'lucide-react';
import { MetricGrid, SectionHeader, StatCard } from '../shared';
import type { SuperAdminContext } from '../types';

export function AccessSection({ ctx }: { ctx: SuperAdminContext }) {
  const accessOrgs = useMemo(() => ctx.accessOrgs, [ctx.accessOrgs]);
  const held = useMemo(() => accessOrgs.filter((o) => o.accountHold), [accessOrgs]);
  const atRisk = useMemo(
    () => accessOrgs.filter((o) => !o.accountHold && o.subscriptionStatus === 'past_due'),
    [accessOrgs]
  );

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Access control"
        subtitle="Hold management for activated workspaces only. Incomplete checkout (pending payment) profiles are excluded."
      />

      <MetricGrid>
        <StatCard label="On hold" value={held.length} tone="danger" icon={<Ban className="h-4 w-4" />} hint="Workspace blocked" />
        <StatCard label="Past due (not held)" value={atRisk.length} tone="warning" icon={<ShieldAlert className="h-4 w-4" />} hint="Candidates for hold" />
        <StatCard label="Allowed access" value={accessOrgs.length - held.length} tone="success" icon={<LockOpen className="h-4 w-4" />} hint="Not on hold" />
      </MetricGrid>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-md">
        <div className="border-b border-zinc-100 bg-zinc-50/90 px-4 py-3">
          <h3 className="text-sm font-semibold text-zinc-900">Hold management</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-100/80 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Hold reason</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {accessOrgs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">
                    No activated workspaces yet.
                  </td>
                </tr>
              ) : (
                accessOrgs.map((o) => (
                  <tr key={o.id} className="hover:bg-zinc-50/90">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-zinc-900">{o.name}</p>
                      <p className="text-xs text-zinc-500">{o.adminEmail}</p>
                    </td>
                    <td className="px-4 py-3 capitalize">{o.subscriptionStatus.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      {o.accountHold ? (
                        <span className="inline-flex items-center gap-1 text-rose-800">
                          <Lock className="h-3.5 w-3.5" /> On hold
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-800">
                          <LockOpen className="h-3.5 w-3.5" /> Allowed
                        </span>
                      )}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-xs text-zinc-600">{o.accountHoldReason ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {o.accountHold ? (
                          <button
                            type="button"
                            onClick={() => void ctx.onReleaseHold(o)}
                            className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-900 hover:bg-emerald-100"
                          >
                            Release hold
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => ctx.onPlaceHold(o)}
                            className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-900 hover:bg-rose-100"
                          >
                            Place hold
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void ctx.onDeleteOrg(o)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-bold text-rose-800 hover:bg-rose-50"
                          title="Delete tenant from database"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
